(() => {
  'use strict';

  const REQUEST_TIMEOUT = 12000;

  class UCCartPageController {
    constructor() {
      this.quantityTimers = new Map();
      this.refreshRequest = null;
      this.upsellRequest = null;
      this.boosterRefreshTimer = null;

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onBoosterUpdate = this.onBoosterUpdate.bind(this);

      document.addEventListener('click', this.onClick);
      document.addEventListener('change', this.onChange);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('submit', this.onSubmit);
      document.addEventListener('booster:cart:update', this.onBoosterUpdate);

      this.syncHeaderCounts();
      this.loadUpsell();
    }

    get root() {
      return document.querySelector('[data-uc-cart-page]');
    }

    get rootUrl() {
      return this.root?.dataset.rootUrl || window.Shopify?.routes?.root || '/';
    }

    cartPayload(extra = {}) {
      return {
        item_count: Number(this.root?.dataset.cartCount || 0),
        total_price: Number(this.root?.dataset.cartTotal || 0),
        currency: this.root?.dataset.cartCurrency || '',
        ...extra,
      };
    }

    emit(name, detail = {}) {
      document.dispatchEvent(new CustomEvent(name, { detail: this.cartPayload(detail) }));
    }

    onClick(event) {
      const quantityButton = event.target.closest('[data-uc-cart-page] [data-uc-cart-quantity-action]');
      if (quantityButton) {
        event.preventDefault();
        const line = quantityButton.closest('[data-uc-cart-line]');
        const input = line?.querySelector('[data-uc-cart-quantity]');
        if (!line || !input || quantityButton.disabled) return;
        const delta = quantityButton.dataset.ucCartQuantityAction === 'increase' ? 1 : -1;
        const quantity = Math.max(0, Number(input.value || 0) + delta);
        input.value = String(quantity);
        this.scheduleLineChange(line.dataset.lineKey, quantity);
        return;
      }

      const removeButton = event.target.closest('[data-uc-cart-page] [data-uc-cart-remove]');
      if (removeButton) {
        event.preventDefault();
        const line = removeButton.closest('[data-uc-cart-line]');
        if (line) this.changeLine(line.dataset.lineKey, 0);
        return;
      }

      if (event.target.closest('[data-uc-cart-page-discount-apply]')) {
        event.preventDefault();
        this.applyDiscount();
        return;
      }

      if (event.target.closest('[data-uc-cart-page-discount-remove]')) {
        event.preventDefault();
        this.removeDiscount();
        return;
      }

      const upsellButton = event.target.closest('[data-uc-page-upsell-add]');
      if (upsellButton) {
        event.preventDefault();
        this.addUpsell(upsellButton);
      }
    }

    onChange(event) {
      const quantityInput = event.target.closest('[data-uc-cart-page] [data-uc-cart-quantity]');
      if (quantityInput) {
        const line = quantityInput.closest('[data-uc-cart-line]');
        const quantity = Math.max(0, Math.floor(Number(quantityInput.value || 0)));
        quantityInput.value = String(quantity);
        if (line) this.scheduleLineChange(line.dataset.lineKey, quantity, 0);
        return;
      }

      const note = event.target.closest('[data-uc-cart-page-note]');
      if (note) {
        this.saveNote(note.value);
        return;
      }

      const variantSelect = event.target.closest('select[data-uc-page-upsell-variant]');
      if (variantSelect) {
        const card = variantSelect.closest('[data-uc-page-upsell-card]');
        const button = card?.querySelector('[data-uc-page-upsell-add]');
        if (button) {
          button.disabled = !variantSelect.value;
          button.textContent = 'Ajouter au panier';
        }
      }
    }

    onKeydown(event) {
      if (event.key !== 'Enter') return;

      if (event.target.matches('[data-uc-cart-page-discount-code]')) {
        event.preventDefault();
        this.applyDiscount();
        return;
      }

      if (event.target.matches('[data-uc-cart-page] [data-uc-cart-quantity]')) {
        event.preventDefault();
        const line = event.target.closest('[data-uc-cart-line]');
        const quantity = Math.max(0, Math.floor(Number(event.target.value || 0)));
        if (line) this.scheduleLineChange(line.dataset.lineKey, quantity, 0);
      }
    }

    onSubmit(event) {
      if (!event.target.matches('[data-uc-cart-page-checkout-form]')) return;
      this.emit('uc:cart:checkout', { source: 'cart_page' });
    }

    onBoosterUpdate() {
      window.clearTimeout(this.boosterRefreshTimer);
      this.boosterRefreshTimer = window.setTimeout(() => {
        this.refreshOrNavigate('booster:cart:update');
      }, 120);
    }

    scheduleLineChange(lineKey, quantity, delay = 200) {
      if (!lineKey) return;
      window.clearTimeout(this.quantityTimers.get(lineKey));
      const timer = window.setTimeout(() => {
        this.quantityTimers.delete(lineKey);
        this.changeLine(lineKey, quantity);
      }, delay);
      this.quantityTimers.set(lineKey, timer);
    }

    async changeLine(lineKey, quantity) {
      const line = this.findLine(lineKey);
      this.setLineBusy(line, true);
      this.clearError();

      try {
        await this.requestJson(`${this.rootUrl}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity }),
        });
        if (!await this.refreshOrNavigate('quantity')) return;
        const eventName = quantity === 0 ? 'uc:cart:remove' : 'uc:cart:change';
        this.emit(eventName, { line_key: lineKey, quantity, source: 'cart_page' });
        this.announce(quantity === 0 ? 'Produit supprimé du panier.' : 'Quantité mise à jour.');

        if (quantity > 0) {
          this.findLine(lineKey)?.querySelector('[data-uc-cart-quantity]')?.focus({ preventScroll: true });
        } else {
          this.root?.querySelector('h1')?.setAttribute('tabindex', '-1');
          this.root?.querySelector('h1')?.focus({ preventScroll: true });
        }
      } catch (error) {
        this.setLineBusy(line, false);
        this.showError(this.errorMessage(error));
        this.announce(this.errorMessage(error));
      }
    }

    findLine(lineKey) {
      return Array.from(this.root?.querySelectorAll('[data-uc-cart-line]') || [])
        .find((line) => line.dataset.lineKey === lineKey);
    }

    setLineBusy(line, busy) {
      if (!line) return;
      line.setAttribute('aria-busy', String(busy));
      line.querySelectorAll('button, input').forEach((control) => {
        control.disabled = busy;
      });
    }

    async refresh() {
      const root = this.root;
      if (!root) {
        const error = new Error('La racine de la page panier est introuvable.');
        error.name = 'CartRenderError';
        throw error;
      }

      const noteWasOpen = Boolean(root.querySelector('.uc-cart-page__note')?.open);
      const discountWasOpen = Boolean(root.querySelector('[data-uc-cart-page-discount]')?.open);
      if (this.refreshRequest) this.refreshRequest.abort();
      const request = new AbortController();
      this.refreshRequest = request;

      try {
        const response = await this.fetchWithTimeout(`${this.rootUrl}cart?section_id=cart-page-premium`, {
          headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
          signal: request.signal,
        });
        if (!response.ok) throw await this.responseError(response);

        const sectionHtml = await response.text();
        const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const nextRoot = parsed.querySelector('[data-uc-cart-page]');
        if (!nextRoot) {
          const error = new Error('Le fragment de page panier est absent de la réponse Shopify.');
          error.name = 'CartRenderError';
          throw error;
        }

        root.replaceWith(nextRoot);
        if (noteWasOpen) this.root?.querySelector('.uc-cart-page__note')?.setAttribute('open', '');
        if (discountWasOpen) this.root?.querySelector('[data-uc-cart-page-discount]')?.setAttribute('open', '');
        this.syncHeaderCounts();
        this.loadUpsell();
        return this.root;
      } finally {
        if (this.refreshRequest === request) this.refreshRequest = null;
      }
    }

    async refreshOrNavigate(source) {
      try {
        return Boolean(await this.refresh());
      } catch (error) {
        this.debugRenderError(source, error);
        if (error?.name === 'AbortError') return false;
        if (!this.navigationFallbackStarted) {
          this.navigationFallbackStarted = true;
          window.location.assign(new URL(`${this.rootUrl}cart`, window.location.origin).href);
        }
        return false;
      }
    }

    debugRenderError(source, error) {
      if (window.console?.warn) {
        console.warn(`[UC Cart] Rendu partiel indisponible (${source}). Rechargement de /cart.`, error);
      }
    }

    syncHeaderCounts() {
      const count = Number(this.root?.dataset.cartCount || 0);
      document.querySelectorAll('.cart-count').forEach((element) => {
        element.textContent = String(count);
        element.hidden = count === 0;
      });
      document.querySelectorAll('[data-uc-cart-open]').forEach((element) => {
        element.setAttribute('aria-label', count > 0
          ? `Panier : ${count} article${count > 1 ? 's' : ''}`
          : 'Panier vide');
      });
    }

    async saveNote(note) {
      const status = this.root?.querySelector('[data-uc-cart-page-note-status]');
      if (status) status.textContent = 'Enregistrement…';

      try {
        await this.requestJson(`${this.rootUrl}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note }),
        });
        if (status) status.textContent = 'Note enregistrée.';
        this.announce('Note enregistrée.');
      } catch (error) {
        if (status) status.textContent = this.errorMessage(error);
        this.showError(this.errorMessage(error));
      }
    }

    async applyDiscount() {
      const details = this.root?.querySelector('[data-uc-cart-page-discount]');
      const input = details?.querySelector('[data-uc-cart-page-discount-code]');
      const button = details?.querySelector('[data-uc-cart-page-discount-apply]');
      const code = input?.value.trim();
      if (!code) {
        this.setDiscountResult('Saisissez un code de réduction.', 'error');
        input?.focus();
        return;
      }

      button.disabled = true;
      details.setAttribute('aria-busy', 'true');
      this.setDiscountResult('Vérification du code…', 'loading');

      try {
        const cart = await this.requestJson(`${this.rootUrl}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: code }),
        });

        const normalizedCode = code.toLocaleLowerCase();
        const discountCodes = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
        const applications = [
          ...(Array.isArray(cart.cart_level_discount_applications) ? cart.cart_level_discount_applications : []),
          ...(Array.isArray(cart.items) ? cart.items : []).flatMap((item) => item.line_level_discount_allocations || []).map((allocation) => allocation.discount_application),
        ];
        const confirmed = discountCodes.some((discount) =>
          String(discount.code || '').toLocaleLowerCase() === normalizedCode && discount.applicable !== false,
        ) || applications.some((application) =>
          String(application?.title || '').toLocaleLowerCase() === normalizedCode,
        );

        if (!await this.refreshOrNavigate('discount')) return;
        const nextDetails = this.root?.querySelector('[data-uc-cart-page-discount]');
        if (nextDetails) nextDetails.open = true;

        if (!confirmed) {
          this.setDiscountResult('Ce code n’est pas applicable à ce panier. Le panier reste intact.', 'error');
          return;
        }

        this.setDiscountResult('Code appliqué et confirmé par Shopify.', 'success');
        this.announce('Code de réduction appliqué.');
      } catch (error) {
        this.setDiscountResult(this.errorMessage(error), 'error');
      } finally {
        const currentDetails = this.root?.querySelector('[data-uc-cart-page-discount]');
        currentDetails?.removeAttribute('aria-busy');
        const currentButton = currentDetails?.querySelector('[data-uc-cart-page-discount-apply]');
        if (currentButton) currentButton.disabled = false;
      }
    }

    async removeDiscount() {
      this.clearError();
      try {
        await this.requestJson(`${this.rootUrl}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: '' }),
        });
        if (!await this.refreshOrNavigate('discount_remove')) return;
        const details = this.root?.querySelector('[data-uc-cart-page-discount]');
        if (details) details.open = true;
        this.setDiscountResult('Code de réduction retiré.', 'success');
        this.announce('Code de réduction retiré.');
      } catch (error) {
        this.showError(this.errorMessage(error));
      }
    }

    setDiscountResult(message, state) {
      const result = this.root?.querySelector('[data-uc-cart-page-discount-result]');
      if (!result) return;
      result.textContent = message;
      result.dataset.state = state;
    }

    async loadUpsell() {
      const container = this.root?.querySelector('[data-uc-cart-page-upsell]');
      const productId = this.root?.dataset.referenceProductId;
      if (!container || !productId) return;
      if (container.dataset.loadedFor === productId) return;

      if (this.upsellRequest) this.upsellRequest.abort();
      this.upsellRequest = new AbortController();
      container.hidden = true;

      try {
        let recommendation = await this.fetchRecommendation(productId, 'complementary', this.upsellRequest.signal);
        if (!recommendation?.querySelector('[data-uc-page-upsell-card]')) {
          recommendation = await this.fetchRecommendation(productId, 'related', this.upsellRequest.signal);
        }

        if (!recommendation?.querySelector('[data-uc-page-upsell-card]')) {
          container.innerHTML = '';
          container.hidden = true;
          return;
        }

        container.innerHTML = recommendation.innerHTML;
        container.hidden = false;
        container.dataset.loadedFor = productId;
      } catch (error) {
        if (error.name !== 'AbortError') container.hidden = true;
      } finally {
        this.upsellRequest = null;
      }
    }

    async fetchRecommendation(productId, intent, signal) {
      const params = new URLSearchParams({
        product_id: productId,
        section_id: 'cart-page-premium',
        intent,
        limit: '6',
      });
      const response = await this.fetchWithTimeout(`${this.rootUrl}recommendations/products?${params}`, {
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
        signal,
      });
      if (!response.ok) return null;
      const html = await response.text();
      return new DOMParser().parseFromString(html, 'text/html').querySelector('[data-uc-cart-page-upsell]');
    }

    async addUpsell(button) {
      const card = button.closest('[data-uc-page-upsell-card]');
      const variantControl = card?.querySelector('[data-uc-page-upsell-variant]');
      const variantId = Number(variantControl?.value || 0);
      if (!variantId) {
        this.showError('Choisissez une taille ou une variante avant l’ajout.');
        variantControl?.focus();
        return;
      }

      button.disabled = true;
      card.setAttribute('aria-busy', 'true');
      this.clearError();

      try {
        await this.requestJson(`${this.rootUrl}cart/add.js?uc_source=upsell`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
        if (!await this.refreshOrNavigate('upsell')) return;
        const detail = {
          product_id: Number(card.dataset.productId || 0),
          variant_id: variantId,
          quantity: 1,
          source: 'cart_page_upsell',
        };
        this.emit('uc:cart:add', detail);
        this.emit('uc:cart:upsell_add', detail);
        this.announce('Produit complémentaire ajouté au panier.');
      } catch (error) {
        button.disabled = false;
        card.removeAttribute('aria-busy');
        this.showError(this.errorMessage(error));
      }
    }

    async requestJson(url, options) {
      const response = await this.fetchWithTimeout(url, options);
      const raw = await response.text();
      let payload;
      try {
        payload = JSON.parse(raw);
      } catch (_) {
        const error = new Error(response.ok ? 'Réponse Shopify invalide. Réessayez.' : `Erreur Shopify ${response.status}.`);
        error.status = response.status;
        throw error;
      }
      if (!response.ok) {
        const error = new Error(payload.description || payload.message || 'La demande Shopify a échoué.');
        error.status = response.status;
        throw error;
      }
      return payload;
    }

    async fetchWithTimeout(url, options = {}) {
      const timeoutController = new AbortController();
      const timeout = window.setTimeout(() => timeoutController.abort(), REQUEST_TIMEOUT);
      const externalSignal = options.signal;
      const abortFromExternal = () => timeoutController.abort();
      if (externalSignal?.aborted) timeoutController.abort();
      else externalSignal?.addEventListener('abort', abortFromExternal, { once: true });

      try {
        return await window.fetch(url, { ...options, signal: timeoutController.signal });
      } catch (error) {
        if (error.name === 'AbortError' && !externalSignal?.aborted) {
          const timeoutError = new Error('La requête a expiré. Vérifiez votre connexion et réessayez.');
          timeoutError.name = 'TimeoutError';
          throw timeoutError;
        }
        throw error;
      } finally {
        window.clearTimeout(timeout);
        externalSignal?.removeEventListener('abort', abortFromExternal);
      }
    }

    async responseError(response) {
      try {
        const payload = await response.json();
        const error = new Error(payload.description || payload.message || `Erreur Shopify ${response.status}.`);
        error.status = response.status;
        return error;
      } catch (_) {
        const error = new Error(`Erreur Shopify ${response.status}.`);
        error.status = response.status;
        return error;
      }
    }

    errorMessage(error) {
      if (error?.status === 422) return error.message || 'Stock insuffisant ou quantité indisponible.';
      if (error?.name === 'AbortError') return 'La requête a été interrompue.';
      if (error?.name === 'CartRenderError' || /page panier|fragment|rendu Shopify/i.test(error?.message || '')) {
        return 'Le panier n’a pas pu être actualisé. Rechargez la page puis réessayez.';
      }
      return error?.message || 'Une erreur réseau est survenue. Réessayez.';
    }

    showError(message) {
      const element = this.root?.querySelector('[data-uc-cart-page-error]');
      if (!element) return;
      const safeMessage = /Shopify n[’']a pas renvoyé|page panier est introuvable|fragment de page panier/i.test(message)
        ? 'Le panier n’a pas pu être actualisé. Rechargez la page puis réessayez.'
        : message;
      element.textContent = safeMessage;
      element.hidden = false;
      element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    clearError() {
      const element = this.root?.querySelector('[data-uc-cart-page-error]');
      if (!element) return;
      element.textContent = '';
      element.hidden = true;
    }

    announce(message) {
      const element = this.root?.querySelector('[data-uc-cart-page-status]');
      if (!element) return;
      element.textContent = '';
      window.setTimeout(() => { element.textContent = message; }, 20);
    }
  }

  function initialize() {
    if (!document.querySelector('[data-uc-cart-page]') || window.UCCartPage) return;
    window.UCCartPage = new UCCartPageController();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
